import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios';
import {
  clearTokens,
  getAccessToken,
  getRefreshToken,
  setAccessToken,
  setRefreshToken,
} from '../auth/tokenStorage';

const API_BASE =
  (typeof __TLN_API_URL__ !== 'undefined' && __TLN_API_URL__) ||
  import.meta.env.VITE_API_URL ||
  '/v1';

export const apiClient = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' },
  withCredentials: false,
});

apiClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = getAccessToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

let refreshPromise: Promise<string | null> | null = null;

/** Restore in-memory access token from sessionStorage refresh token (boot + 401 retry). */
export async function restoreAccessTokenFromRefresh(): Promise<string | null> {
  const refresh = getRefreshToken();
  if (!refresh) return null;
  try {
    const { data } = await axios.post<{ accessToken: string; refreshToken?: string }>(
      `${API_BASE}/auth/refresh`,
      { refreshToken: refresh },
    );
    setAccessToken(data.accessToken);
    if (data.refreshToken) setRefreshToken(data.refreshToken);
    return data.accessToken;
  } catch {
    clearTokens();
    return null;
  }
}

apiClient.interceptors.response.use(
  (res) => res,
  async (error: AxiosError) => {
    const original = error.config as InternalAxiosRequestConfig & { _retry?: boolean };
    if (error.response?.status === 401 && original && !original._retry) {
      original._retry = true;
      if (!refreshPromise) {
        refreshPromise = restoreAccessTokenFromRefresh().finally(() => {
          refreshPromise = null;
        });
      }
      const newToken = await refreshPromise;
      if (newToken) {
        original.headers.Authorization = `Bearer ${newToken}`;
        return apiClient(original);
      }
      window.dispatchEvent(new CustomEvent('tln:auth-expired'));
    }
    return Promise.reject(error);
  },
);

export function getApiBaseUrl(): string {
  return API_BASE;
}

export function getApiOrigin(): string {
  return API_BASE.replace(/\/v1\/?$/, '');
}

export function resolveMediaUrl(path: string | null | undefined): string | null {
  if (!path) return null;
  if (/^https?:\/\//i.test(path)) {
    try {
      const url = new URL(path);
      if (url.pathname.includes('/media/avatars/')) {
        return `${url.pathname}${url.search}`;
      }
    } catch {
      return path;
    }
    return path;
  }
  let normalized = path.startsWith('/') ? path : `/${path}`;
  if (normalized.startsWith('/media/')) {
    normalized = `/v1${normalized}`;
  }
  if (import.meta.env.DEV) {
    return normalized;
  }
  const origin = getApiOrigin().replace(/\/$/, '');
  return `${origin}${normalized}`;
}
